import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

/**
 * Per-item recurring-rental autopay.
 *
 * - useRentalItems(): the LIVE roster of the customer's rental items, read from
 *   orders.v_customer_rental_items (derived from Tank Rental / Equipment Rental
 *   invoice lines, RLS-scoped to the current customer).
 * - useRentalItemEnrollments(): the customer's rows in orders.rental_autopay_items.
 * - useToggleRentalItem(): upsert an enrollment (enabled true/false). Customer-
 *   owned rows under RLS.
 *
 * Semantics: an enrolled item that appears on either a Tank Rental or an
 * Equipment Rental invoice line is in scope to be auto-charged by the executor
 * (leasing app / tank system), which reads orders.v_autopay_item_enrollments.
 */

export type RentalItemKind = 'tank' | 'equipment';

export interface RentalItem {
  itemKey: string;
  itemName: string | null;
  kind: RentalItemKind;
  lastSeen: string | null;
  latestUnitPrice: number | null;
  latestAmount: number | null;
  occurrences: number;
}

interface RentalItemDbRow {
  item_key: string;
  item_name: string | null;
  kind: RentalItemKind;
  last_seen: string | null;
  latest_unit_price: number | string | null;
  latest_amount: number | string | null;
  occurrences: number | string | null;
}

const ROSTER_COLS =
  'item_key, item_name, kind, last_seen, latest_unit_price, latest_amount, occurrences';

/** Live rental-item roster for the signed-in customer. */
export function useRentalItems() {
  return useQuery<RentalItem[]>({
    queryKey: ['rental-items', 'roster'],
    queryFn: async () => {
      const { data, error } = await supabase
        .schema('orders')
        .from('v_customer_rental_items')
        .select(ROSTER_COLS)
        .order('last_seen', { ascending: false });
      if (error) {
        // View not yet pushed or no rental history — degrade to empty.
        return [];
      }
      return (data ?? []).map((r): RentalItem => {
        const row = r as RentalItemDbRow;
        return {
          itemKey: row.item_key,
          itemName: row.item_name,
          kind: row.kind,
          lastSeen: row.last_seen,
          latestUnitPrice: row.latest_unit_price == null ? null : Number(row.latest_unit_price),
          latestAmount: row.latest_amount == null ? null : Number(row.latest_amount),
          occurrences: Number(row.occurrences ?? 0),
        };
      });
    },
    staleTime: 60 * 1000,
  });
}

export interface RentalItemEnrollment {
  itemKey: string;
  kind: RentalItemKind | null;
  enabled: boolean;
}

interface RentalItemEnrollmentDbRow {
  item_key: string;
  kind: RentalItemKind | null;
  enabled: boolean;
}

/** Current per-item autopay enrollments for the signed-in customer (RLS-scoped). */
export function useRentalItemEnrollments() {
  return useQuery<RentalItemEnrollment[]>({
    queryKey: ['rental-autopay-items'],
    queryFn: async () => {
      const { data, error } = await supabase
        .schema('orders')
        .from('rental_autopay_items')
        .select('item_key, kind, enabled');
      if (error) return [];
      return (data ?? []).map((r): RentalItemEnrollment => {
        const row = r as RentalItemEnrollmentDbRow;
        return { itemKey: row.item_key, kind: row.kind, enabled: row.enabled };
      });
    },
    staleTime: 30 * 1000,
  });
}

export interface ToggleRentalItemInput {
  customerId: string;
  itemKey: string;
  itemName?: string | null;
  kind: RentalItemKind | null;
  enabled: boolean;
}

/**
 * Turn a single rental item's recurring autopay on/off. Upserts on
 * (customer_id, item_key) so re-toggling the same item updates in place.
 * Customer-owned rows under RLS.
 */
export function useToggleRentalItem() {
  const qc = useQueryClient();
  return useMutation<void, Error, ToggleRentalItemInput>({
    mutationFn: async ({ customerId, itemKey, itemName, kind, enabled }) => {
      const { error } = await supabase
        .schema('orders')
        .from('rental_autopay_items')
        .upsert(
          {
            customer_id: customerId,
            item_key: itemKey,
            item_name: itemName ?? null,
            kind,
            enabled,
          },
          { onConflict: 'customer_id,item_key' },
        );
      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['rental-autopay-items'] });
      qc.invalidateQueries({ queryKey: ['rental-items'] });
    },
  });
}
